import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import { useToast } from "@/hooks/use-toast";
import type { UserProfile } from "@/pages/users/types/user";
import {
  buildReschedulePaymentLink,
  type RescheduleFeeLine,
} from "@/features/users/utils/build-therapy-reschedule-payment-link";
import { Link2 } from "lucide-react";
import { useMemo } from "react";
import type { ComponentProps } from "react";

type ButtonProps = ComponentProps<typeof Button>;

const LABEL: Record<RescheduleFeeLine, string> = {
  therapy: "Copy therapy reschedule fee",
  clinical: "Copy clinical reschedule",
  psychiatry: "Copy psychiatry reschedule",
};

const TOAST_DESCRIPTION: Record<RescheduleFeeLine, string> = {
  therapy: "Therapy reschedule payment link copied to clipboard",
  clinical: "Clinical reschedule payment link copied to clipboard",
  psychiatry: "Psychiatry reschedule payment link copied to clipboard",
};

interface CopyReschedulePaymentLinkButtonProps {
  user: UserProfile;
  line: RescheduleFeeLine;
  className?: string;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
}

export function CopyReschedulePaymentLinkButton({
  user,
  line,
  className,
  variant = "outline",
  size = "sm",
}: CopyReschedulePaymentLinkButtonProps) {
  const { toast } = useToast();
  const url = useMemo(
    () => buildReschedulePaymentLink(user, line),
    [user, line],
  );

  const copy = () => {
    void navigator.clipboard.writeText(url);
    toast({ title: "Copied", description: TOAST_DESCRIPTION[line] });
  };

  return (
    <HasAccess permission="write">
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        onClick={copy}
      >
        <Link2 className="h-4 w-4 mr-1.5" />
        {LABEL[line]}
      </Button>
    </HasAccess>
  );
}
